import { datadogRum } from "@datadog/browser-rum";
import { isNil } from "@clipboard-health/util-ts";
import { addHours, isAfter, isEqual, isValid } from "date-fns";

import { CbhFeatureFlag } from "../FeatureFlags";

export const LOGIN_FLOW = {
  LEGACY: "legacy",
  REDESIGNED: "redesigned",
} as const;

export type LoginFlow = (typeof LOGIN_FLOW)[keyof typeof LOGIN_FLOW];

const LOGIN_FLOW_STORAGE_KEY = "login_flow_telemetry_cohort";
const LOGIN_FLOW_STORAGE_TTL_HOURS = 1;

interface StoredLoginFlow {
  expiresAt: number;
  loginFlow: LoginFlow;
}

interface RecordLoginFlowFeatureFlagEvaluationParams {
  key: string;
  value: unknown;
}

/**
 * Adds an explicit cohort to RUM events because Datadog's feature-flag
 * enrichment is not present on custom login success actions.
 */
export function recordLoginFlowFeatureFlagEvaluation({
  key,
  value,
}: RecordLoginFlowFeatureFlagEvaluationParams): void {
  if (key !== CbhFeatureFlag.REDESIGNED_WORKPLACE_LOGIN_FLOW) {
    return;
  }

  if (typeof value !== "boolean") {
    datadogRum.removeGlobalContextProperty("loginFlow");
    removeStoredLoginFlow();
    return;
  }

  const loginFlow = value ? LOGIN_FLOW.REDESIGNED : LOGIN_FLOW.LEGACY;
  const storedLoginFlow: StoredLoginFlow = {
    expiresAt: addHours(new Date(), LOGIN_FLOW_STORAGE_TTL_HOURS).getTime(),
    loginFlow,
  };

  datadogRum.setGlobalContextProperty("loginFlow", loginFlow);
  storeLoginFlow(storedLoginFlow);
}

/** Returns the recent cohort after a magic-link or SSO page handoff. */
export function getStoredLoginFlowTelemetryCohort(): LoginFlow | undefined {
  const storedValue = readStoredLoginFlow();
  if (isNil(storedValue)) {
    return undefined;
  }

  if (storedValue.length === 0) {
    removeStoredLoginFlow();
    return undefined;
  }

  try {
    const parsedValue: unknown = JSON.parse(storedValue);
    if (!isStoredLoginFlow(parsedValue)) {
      removeStoredLoginFlow();
      return undefined;
    }

    const expiresAt = new Date(parsedValue.expiresAt);
    const now = new Date();
    if (!isValid(expiresAt) || isAfter(now, expiresAt) || isEqual(now, expiresAt)) {
      removeStoredLoginFlow();
      return undefined;
    }

    return parsedValue.loginFlow;
  } catch {
    removeStoredLoginFlow();
    return undefined;
  }
}

/**
 * Resolves the login-flow cohort for RUM enrichment: the RUM global `loginFlow`
 * when it is a known cohort, otherwise the stored fallback that survives the
 * magic-link/SSO page handoff. Takes the caller's already-read global value so
 * hot-path callers don't trigger a second `getGlobalContext()` read.
 */
export function resolveLoginFlowCohort(candidate: unknown): LoginFlow | undefined {
  if (candidate === LOGIN_FLOW.LEGACY || candidate === LOGIN_FLOW.REDESIGNED) {
    return candidate;
  }

  return getStoredLoginFlowTelemetryCohort();
}

function isStoredLoginFlow(value: unknown): value is StoredLoginFlow {
  if (typeof value !== "object" || isNil(value)) {
    return false;
  }

  if (!("expiresAt" in value) || !("loginFlow" in value)) {
    return false;
  }

  return (
    typeof value.expiresAt === "number" &&
    (value.loginFlow === LOGIN_FLOW.LEGACY || value.loginFlow === LOGIN_FLOW.REDESIGNED)
  );
}

function readStoredLoginFlow(): string | undefined {
  try {
    return localStorage.getItem(LOGIN_FLOW_STORAGE_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

function storeLoginFlow(loginFlow: StoredLoginFlow): void {
  try {
    localStorage.setItem(LOGIN_FLOW_STORAGE_KEY, JSON.stringify(loginFlow));
  } catch {
    // RUM enrichment is best-effort and must not block login.
  }
}

function removeStoredLoginFlow(): void {
  try {
    localStorage.removeItem(LOGIN_FLOW_STORAGE_KEY);
  } catch {
    // RUM enrichment is best-effort and must not block login.
  }
}
